// eslint-disable-next-line import/no-cycle
import { getGoogleLatLng } from '../utils/AddressUtil';
import { validateWithGeoCoder, getLatLngGeocoder, validateAddressUnitNumber } from './graphQlSvc';

const innerSetAddressField = (type, field, value, candidateLatLng, useLatin) => ({
    type,
    field,
    value,
    candidateLatLng,
    useLatin,
    persist: (existingActions, newAction) => {
        const otherFields = [
            ...existingActions.filter(
                (existingAction) => existingAction.field !== newAction.field
            )
        ];
            // Only include this action if we actually set a value
        return value ? [...otherFields, newAction] : otherFields;
    }
});

export const LATLNG_PROVIDERS = {
    GOOGLE: 'google',
    GEOCODER: 'geocoder'
};

const LocationActions = {
    SET_LAT_LNG: 'SET_LAT_LNG',
    SET_TYPEAHEAD_QUERY: 'SET_TYPEAHEAD_QUERY',
    SET_TYPEAHEAD_PREDICTIONS: 'SET_TYPEAHEAD_PREDICTIONS',
    SET_PARTIAL_MATCH: 'SET_PARTIAL_MATCH',

    SET_ADDRESS_FIELD: 'SET_ADDRESS_FIELD',
    SET_ADDRESS_VALIDATION: 'SET_ADDRESS_VALIDATION',
    CLEAR_ADDRESS_VALIDATION: 'CLEAR_ADDRESS_VALIDATION',
    CLEAR_ADDRESS_FIELDS: 'CLEAR_ADDRESS_FIELDS',
    CLEAR_PROPERTY_NAME: 'CLEAR_PROPERTY_NAME',

    VALIDATE_GEOCODER: 'VALIDATE_GEOCODER',
    VALIDATE_GEOCODER_START: 'VALIDATE_GEOCODER_START',
    VALIDATE_GEOCODER_SUCCESS: 'VALIDATE_GEOCODER_SUCCESS',
    VALIDATE_GEOCODER_FAIL: 'VALIDATE_GEOCODER_FAIL',
    VALIDATE_GEOCODER_CLEAR: 'VALIDATE_GEOCODER_CLEAR',

    SERVER_VALIDATION_SET: 'SERVER_VALIDATION_SET',
    SERVER_VALIDATION_CLEAR: 'SERVER_VALIDATION_CLEAR',

    SET_PROPERTY_NAME: 'SET_PROPERTY_NAME_LOCATION',

    TRIGGER_MIN_COORDINATES: 'TRIGGER_MIN_COORDINATES',

    SET_PIN_LOCATION: 'SET_PIN_LOCATION',

    setPinLocation: (latLng) => ({
        type: LocationActions.SET_PIN_LOCATION,
        latLng,
        persist: true
    }),

    triggerMinCoordinates: (country) => ({
        type: LocationActions.TRIGGER_MIN_COORDINATES,
        country
    }),

    setPropertyName: (name) => ({
        type: LocationActions.SET_PROPERTY_NAME,
        name
    }),

    setServerAddresValidation: (validation) => ({
        type: LocationActions.SERVER_VALIDATION_SET,
        validation
    }),
    clearServerAddressValidation: () => ({
        type: LocationActions.SERVER_VALIDATION_CLEAR
    }),

    geocoderValidateClear: () => ({
        type: LocationActions.VALIDATE_GEOCODER_CLEAR
    }),

    geocoderValidate: ({
        address, latLng, latLngFallback, propertyName, pathSource
    }) => {
        const start = () => ({ type: LocationActions.VALIDATE_GEOCODER_START });
        const success = ({ value, errorList }) => ({
            type: LocationActions.VALIDATE_GEOCODER_SUCCESS,
            value,
            errorList
        });
        const fail = () => ({ type: LocationActions.VALIDATE_GEOCODER_FAIL, errorList: ['validationFailed'] });
        return validateWithGeoCoder(
            {
                address, latLng, latLngFallback, propertyName, pathSource
            },
            start,
            success,
            fail
        );
    },

    GEOCODER_LAT_LNG: 'GEOCODER_LAT_LNG',
    GEOCODER_LAT_LNG_START: 'GEOCODER_LAT_LNG_START',
    GEOCODER_LAT_LNG_SUCCESS: 'GEOCODER_LAT_LNG_SUCCESS',
    GEOCODER_LAT_LNG_FAIL: 'GEOCODER_LAT_LNG_FAIL',
    GEOCODER_LAT_LNG_VALUE: 'GEOCODER_LAT_LNG_VALUE',
    GEOCODER_LAT_LNG_CLEAR: 'GEOCODER_LAT_LNG_CLEAR',

    clearGeocoderLatLng: () => ({
        type: LocationActions.GEOCODER_LAT_LNG_CLEAR
    }),

    geocoderLatLng: ({
        address, candidateLatLng, propertyName, pathSource, provider, minCoordinates
    }) => {
        const start = () => ({ type: LocationActions.GEOCODER_LAT_LNG_START });
        const success = ({
            lat, lng, error, threshold
        }) => ({
            type: LocationActions.GEOCODER_LAT_LNG_SUCCESS,
            value: {
                lat,
                lng
            },
            error,
            threshold
        });
        const fail = () => ({ type: LocationActions.GEOCODER_LAT_LNG_FAIL, error: 'latLngFailed' });
        switch (provider) {
        case LATLNG_PROVIDERS.GOOGLE:
            return getGoogleLatLng(
                {
                    address, candidateLatLng, propertyName, pathSource, minCoordinates
                },
                start,
                success,
                fail
            );

        default:
            return getLatLngGeocoder(
                {
                    address, candidateLatLng, propertyName, pathSource, minCoordinates
                },
                start,
                success,
                fail
            );
        }
    },

    triggerGeoCoderValidation: () => ({
        type: LocationActions.VALIDATE_GEOCODER
    }),

    triggerGeocoderLatLng: ({ candidateLatLng, country }) => ({
        type: LocationActions.GEOCODER_LAT_LNG,
        candidateLatLng,
        country,
        meta: {
            throttle: 500
        }
    }),

    VALIDATE_UNIT_NUMBER: 'VALIDATE_UNIT_NUMBER',
    VALIDATE_UNIT_NUMBER_START: 'VALIDATE_UNIT_NUMBER_START',
    VALIDATE_UNIT_NUMBER_SUCCESS: 'VALIDATE_UNIT_NUMBER_SUCCESS',
    VALIDATE_UNIT_NUMBER_FAIL: 'VALIDATE_UNIT_NUMBER_FAIL',
    VALIDATE_UNIT_NUMBER_CLEAR: 'VALIDATE_UNIT_NUMBER_CLEAR',

    setLatLng: (latLng) => ({
        type: LocationActions.SET_LAT_LNG,
        latLng,
        persist: true
    }),

    setTypeaheadQuery: (query) => ({
        type: LocationActions.SET_TYPEAHEAD_QUERY,
        query,
        persist: true
    }),

    setTypeaheadPredictions: (predictions) => ({
        type: LocationActions.SET_TYPEAHEAD_PREDICTIONS,
        predictions,
        persist: true
    }),

    setAddressField: (field, value, useLatin = false, candidateLatLng) => innerSetAddressField(
        LocationActions.SET_ADDRESS_FIELD,
        field,
        value,
        candidateLatLng,
        useLatin
    ),

    setAddressValidation: (validation) => ({
        type: LocationActions.SET_ADDRESS_VALIDATION,
        validation
    }),

    clearAddressValidation: () => ({
        type: LocationActions.CLEAR_ADDRESS_VALIDATION
    }),
    clearAddressFields: () => ({
        type: LocationActions.CLEAR_ADDRESS_FIELDS
    }),
    clearPropertyName: () => ({
        type: LocationActions.CLEAR_PROPERTY_NAME
    }),

    clearAddress: () => (dispatch) => {
        // Use thunk to reset fields individually, so local storage is synced
        dispatch(LocationActions.clearAddressFields());
        dispatch(LocationActions.clearAddressValidation());
        dispatch(LocationActions.clearPropertyName());
    },

    validateUnitNumber: (address) => {
        const start = () => ({ type: LocationActions.VALIDATE_UNIT_NUMBER_START });
        const success = (value) => ({
            type: LocationActions.VALIDATE_UNIT_NUMBER_SUCCESS,
            value
        });
        const fail = () => ({ type: LocationActions.VALIDATE_UNIT_NUMBER_FAIL });
        return validateAddressUnitNumber(address, start, success, fail);
    },

    validateUnitNumberClear: () => ({
        type: LocationActions.VALIDATE_UNIT_NUMBER_CLEAR
    }),

    triggerUnitNumberValidation: () => ({
        type: LocationActions.VALIDATE_UNIT_NUMBER
    })
};

export default LocationActions;
